<template>
  <custom-callout
    :border-color="statusColor.badged"
    class-main="mt-3 mb-n1"
    :controlled="true"
    :opened="data.isOpen"
    @click:open="onCloseHandler"
  >
    <div class="row py-2 mr-md-0 align-items-center">
      <div id="statusBadge" class="col-12 col-md-3 text-center">
        <div class="d-flex flex-column font-weight-bold color-black">
          <span
            :class="
              `mb-1 badge badge-pill font-14 pt-2 pb-2 w-70 mx-auto badge-${statusColor.badged}`
            "
          >
            {{ data.serviceStatusDescription | empty }}
          </span>
          <span class="font-16">
            {{ data.requestNumber | empty }}
          </span>
          <span
            v-if="isReturnedToSAEEByHiringLack(data)"
            class="d-flex font-12 mr-2 mt-3 color-secondary-40"
          >
            <span class="align-self-center bell fa fa-bell-exclamation" />
            <span>{{ data.determinationReasonDescription }}</span>
          </span>
          <span
            v-if="data.isReconsideration"
            class="d-flex mx-auto font-12 mr-2 mt-3 color-secondary-40"
          >
            <span class="align-self-center bell fa fa-bell-exclamation" />
            <span>Solicitud de Reconsideración</span>
          </span>
        </div>
      </div>
      <div id="statusCircleBadge" class="col-12 col-md-2 text-left d-none">
        <div class="row">
          <div class="col col-sm col-10 font-weight-bold color-black p-3">
            <div class="row">
              <span class="col col-sm col-12 font-16">
                {{ data.requestNumber | empty }}
              </span>
              <div class="col col-sm col-12 d-inline-block">
                <span class="circle-dot mr-2" :style="`${statusColor.color}`" />
                <span class="text-secondary">
                  {{ data.serviceStatusDescription | empty }}
                </span>
              </div>
            </div>
          </div>
          <div class="col col-sm col-2">
            <div
              v-if="showPrintApproval"
              class="p-3 rounded pt-2 cursor-pointer d-inline-block float-right mr-2"
              :class="{
                'bg-secondary-38': !data.approvedForPrinting,
                'bg-green': data.approvedForPrinting,
              }"
              @click="onMarkAsPrintedHandler(data)"
            >
              <em
                class="font-12 p-1"
                :class="{
                  'color-gray-6 far fa-flag': !data.approvedForPrinting,
                  'color-white fas fa-flag': data.approvedForPrinting,
                }"
              />
            </div>
          </div>
        </div>
        <hr class="d-none" />
      </div>
      <div class="col-md-8">
        <div class="row mb-3">
          <div class="col-12 col-md-4">
            <span class="data-title d-block">Nombre del Estudiante:</span>
            <span class="font-14 color-black">
              {{ data.studentNameSie | empty }}
            </span>
          </div>
          <div class="col-12 col-md-4">
            <span class="data-title d-block">Servicio:</span>
            <span class="font-14 color-black">
              {{ data.serviceDiscipline | empty }}
            </span>
          </div>
          <div class="col-12 col-md-4">
            <span class="data-title d-block">Fecha de Solicitud:</span>
            <span class="font-14 color-black">
              {{ data.requestDate | date('DD/MM/YYYY') }}
            </span>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-4">
            <span class="data-title d-block">Fecha de Vencimiento:</span>
            <span class="font-14 color-black">
              {{ data.determinationDate | date('DD/MM/YYYY') }}
            </span>
          </div>
          <div class="col-12 col-md-4">
            <span class="data-title d-block">Días de Vencimiento:</span>
            <span
              v-if="expired"
              class="font-14 font-weight-bold badge badge-pill badge-error color-red border-solid-1 px-3 py-2"
            >
              {{ data.daysCount }} días
            </span>
            <span
              v-else
              class="font-14 font-weight-bold badge badge-pill badge-success color-green border-solid-1 px-3 py-2"
            >
              {{ data.daysCount }} días
            </span>
          </div>
          <div class="col-12 col-md-4">
            <span class="data-title d-block">Nombre del Solicitante:</span>
            <span class="font-14 color-black">
              {{ data.applicantName | empty }}
            </span>
          </div>
        </div>
        <hr class="d-none" />
      </div>
      <div
        id="buttonsContainer"
        class="col-12 col-md-1 mt-3 mt-md-0 d-flex align-items-end justify-content-end"
      >
        <div
          v-if="showPrintApproval"
          id="printApproval"
          class="d-flex justify-content-center align-items-center p-2 rounded pt-2 cursor-pointer"
          :class="{
            'bg-secondary-38': !data.approvedForPrinting,
            'bg-green': data.approvedForPrinting,
          }"
          style="height: 38px"
          @click="onMarkAsPrintedHandler(data)"
        >
          <em
            class="font-12 p-1"
            :class="{
              'color-gray-6 far fa-flag': !data.approvedForPrinting,
              'color-white fas fa-flag': data.approvedForPrinting,
            }"
          />
        </div>
        <drop-down class="ml-2" :items="actionItems" />
      </div>
    </div>
    <template #edition>
      <appointment-notification :data="data" />
      <div class="p-2 mt-1">
        <div class="bg-white rounded p-md-3 px-md-4">
          <custom-tab v-if="showCustomTab" class="custom-tabs">
            <custom-tab-item
              active
              lazy
              title="Historial de Estatus"
              @click="onOpenStatusHistory()"
            >
              <request-service-status-history
                :items="data.serviceStatusHistory.data"
                :page-count="data.serviceStatusHistory.pageCount"
                @on-load-more="$emit('on-status-history-load-more')"
              />
            </custom-tab-item>
            <custom-tab-item
              :active="
                data.viewToDisplay ===
                  requestHistoryViewDisplay.CONTRACT_HISTORY
              "
              lazy
              title="Historial de Contrato"
              @click="onOpenContractHistory()"
            >
              <request-service-contract-history
                :items="serviceContractHistory"
                @download-item="onDownloadDocumentHandler"
              >
                <template #pagination>
                  <pagination
                    v-if="showServiceContractPagination"
                    :data="serviceContractPagination"
                    :page-size="serviceContractPagination.pageSize"
                    :show-text="true"
                    @pagination-go-page="onChangePageHandler"
                  />
                </template>
              </request-service-contract-history>
            </custom-tab-item>
            <custom-tab-item
              v-if="canViewDetermination"
              :active="determinationPanelActive"
              lazy
              title="Determinación"
              @click="onOpenViewDeterminationHandler()"
            >
              <div class="pt-3">
                <RequestServiceDetermination
                  :academic-years="academicYears"
                  :data="data"
                  :is-read-only="true"
                  @save="onDeterminationSave()"
                />
              </div>
            </custom-tab-item>
            <custom-tab-item
              :active="
                data.viewToDisplay === requestHistoryViewDisplay.DOCUMENT
              "
              lazy
              title="Documentos de la Solicitud"
              @click="onOpenRequestDocument()"
            >
              <request-document
                :items="requestDocument"
                @on-download-document="onDownloadDocumentHandler"
              />
            </custom-tab-item>
            <custom-tab-item
              v-if="canSeeBinnacle"
              :active="
                data.viewToDisplay === requestHistoryViewDisplay.BITACORA
              "
              lazy
              title="Bitácora de Seguimiento"
            >
              <RequestDetail
                :request-number="data.requestNumber"
                :status-color="statusColor.hexa"
                :status-name="data.serviceStatusDescription"
              />
              <AddBlogItem
                :comment-types="commentTypes"
                :request-id="data.requestServiceId"
                @on-open-close="$emit('on-open-close')"
                @save="$emit('save-blog-item', $event)"
              />
            </custom-tab-item>
            <custom-tab-item
              v-if="canSeeBinnacle"
              :active="
                data.viewToDisplay ===
                  requestHistoryViewDisplay.BITACORA_HISTORY
              "
              lazy
              title="Hitorial de Bitácora"
            >
              <RequestDetail
                :request-number="data.requestNumber"
                :status-color="statusColor.hexa"
                :status-name="data.serviceStatusDescription"
              />
              <BitacoraHistory
                :blog-items="blogItems"
                @download="onDownloadDocumentHandler({ documentId: $event })"
                @load-bitacora="fetchBitacoraHistory(data.requestServiceId)"
              />
            </custom-tab-item>
          </custom-tab>
          <div v-if="!showCustomTab" class="py-3">
            <RequestServiceDetermination
              :academic-years="academicYears"
              :data="data"
              :is-read-only="false"
              @cancel="onCloseHandler"
              @save="onServiceSaved($event, data)"
            />
          </div>
        </div>
      </div>
    </template>
  </custom-callout>
</template>

<script>
import alert from 'utils/mixins/alert.mixin.js';
import CustomCallout from 'custom-callout';
import DropDown from 'dropdown';
import Pagination from 'pagination';
import CustomTab from 'custom-tabs/src/CustomTab';
import CustomTabItem from 'custom-tabs/src/CustomTabItem';
import RequestServiceStatusHistory from './RequestServiceStatusHistory.vue';
import RequestServiceContractHistory from './RequestServiceContractHistory.vue';
import RequestDocument from './RequestDocument.vue';
import ServiceStatuses from '@/utils/constants/requestServiceStatuses';
import ServiceStatusColors from '@/utils/constants/requestServiceStatusColors';
import RequestServiceDetermination from './RequestServiceDetermination.vue';
import requestHistoryViewDisplay from '@/utils/constants/requestHistoryViewDisplay.js';
import determinationReasons from '@/utils/constants/determinationReasons.js';
import ServiceTypes from '@/utils/constants/serviceTypes';
import AppointmentNotification from './AppointmentNotification.vue';
import AddBlogItem from './AddBlogItem.vue';
import RequestDetail from './RequestDetail.vue';
import BitacoraHistory from './BitacoraHistory.vue';
import { getRequestServicesById } from '@/services/uspqrp/provisional-remedy.api';
import { downloadDocumentByAttachmentId as _downloadDocumentByAttachmentId } from '@/services/common/document.api.js';
import { getBitacoraItems } from '@/services/uspqrp/provisional-remedy.api';

const DEFAULT_HISTORY_CONTRACT_PAGE_SIZE = 5;
export default {
  name: 'RequestServiceResult',
  components: {
    CustomCallout,
    DropDown,
    CustomTab,
    CustomTabItem,
    Pagination,
    RequestServiceDetermination,
    RequestServiceStatusHistory,
    RequestServiceContractHistory,
    RequestDocument,
    AppointmentNotification,
    RequestDetail,
    AddBlogItem,
    BitacoraHistory,
  },
  mixins: [alert],
  props: {
    data: {
      type: Object,
      required: true,
      default: () => {},
    },
    academicYears: {
      type: Array,
      required: true,
      default: () => [],
    },
    commentTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showCustomTab: true,
      showDeterminationPanel: false,
      blogItems: [],
    };
  },
  computed: {
    actionItems() {
      return [
        {
          label: 'Ver Solicitud',
          icon: 'fas fa-file-import color-green',
          border: 'bottom',
          click: () => this.$emit('on-view-request'),
        },
        {
          label: 'Ver Determinación',
          icon: 'fas fa-eye color-green',
          border: this.canApproveService ? '' : 'bottom',
          visible: this.canViewDetermination,
          click: () => this.onOpenViewDeterminationHandler(),
        },
        {
          label: 'Aprobar',
          icon: 'fas fa-check-circle color-green',
          border:
            this.canCancelService || this.canObjectService ? '' : 'bottom',
          visible: this.canApproveService,
          click: () => {
            this.showCustomTab = false;
            this.$emit(
              'on-open-change-status',
              ServiceStatuses.Approved,
              this.serviceType
            );
          },
        },
        {
          label: 'Aprobar Asistente',
          icon: 'fas fa-check-circle color-green',
          border:
            this.canCancelService || this.canObjectService ? '' : 'bottom',
          visible: this.canApproveServiceAssistant,
          click: () => {
            this.showCustomTab = false;
            this.$emit(
              'on-open-change-status',
              ServiceStatuses.Approved,
              this.serviceType
            );
          },
        },
        {
          label: 'Objetar',
          icon: 'fas fa-gavel color-red',
          border: this.canCancelService ? '' : 'bottom',
          visible: this.canObjectService,
          click: () => {
            this.showCustomTab = false;
            this.$emit(
              'on-open-change-status',
              ServiceStatuses.Objected,
              this.serviceType
            );
          },
        },
        {
          label: 'Cancelar',
          icon: 'fas fa-times-circle color-red',
          visible: this.canCancelService,
          click: () => {
            this.showCustomTab = false;
            this.$emit(
              'on-open-change-status',
              ServiceStatuses.Cancelled,
              this.serviceType
            );
          },
        },
        {
          label: 'Regresar Estatus',
          icon: 'fas fa-undo color-green',
          border: this.canReturnToNewStatus ? '' : 'bottom',
          visible: !this.canReturnStatus && this.canReturnToPreviousStatus,
          click: () => {
            this.showCustomTab = false;
            this.$emit('on-open-return-to-previous-status', false);
          },
        },
        {
          label: 'Regresar Estatus Nuevo',
          icon: 'fas fa-undo color-green',
          border: 'bottom',
          visible: this.canReturnToNewStatus,
          click: () => {
            this.showCustomTab = false;
            this.$emit('on-open-return-to-previous-status', true);
          },
        },
        {
          label: 'Historial de Solicitud',
          icon: 'fas fa-clock color-green',
          click: () => this.onOpenStatusHistory(),
        },
        {
          label: 'Historial de Contrato',
          icon: 'fas fa-history color-green',
          click: () => this.onOpenContractHistory(),
        },
        {
          label: 'Historial de Bitácora',
          icon: 'fas fa-history color-green',
          border: 'bottom',
          click: () => this.$emit('open-bitacora-history', this.data.requestId),
        },
        {
          label: 'Documentos',
          icon: 'fas fa-file-search color-green mr-n1 pr-2',
          border: 'bottom',
          click: () => this.onOpenRequestDocument(),
        },
        {
          label: 'Imprimir Carta',
          icon: 'fas fa-file-download color-green',
          visible: this.canDownloadLetter,
          click: () => this.$emit('on-get-determination-letter'),
        },
        {
          label: 'Descargar Solicitud',
          icon: 'fas fa-file-download color-green',
          border: 'bottom',
          click: () => this.$emit('on-get-request-service-report'),
        },
        {
          label: 'Bitácora de Seguimiento RP',
          icon: 'fas fa-file-signature color-green',
          click: () => this.$emit('open-bitacora'),
        },
        {
          label: 'Solicitar Reconsideración',
          icon: 'fas fa-reply-all gold-orange',
          visible: this.canRequestReconsideration,
          click: () => {
            this.showCustomTab = false;
            this.$emit('on-open-request-reconsideration');
          },
        },
      ];
    },
    statusColor() {
      switch (this.data.serviceStatusId) {
        case ServiceStatuses.New:
          return {
            badged: ServiceStatusColors.Yellow,
            color: 'background-color: #f8ea80',
            hexa: '#f8ea8',
          };
        case ServiceStatuses.Approved:
          return {
            badged: ServiceStatusColors.GreenLight,
            color: 'background-color: #A3E5B3;',
            hexa: '#A3E5B3',
          };
        case ServiceStatuses.Cancelled:
          return {
            badged: ServiceStatusColors.Brown,
            color: 'background-color: #d5b89a;',
            hexa: '#d5b89a',
          };
        case ServiceStatuses.Objected:
          return {
            badged: ServiceStatusColors.Purple,
            color: 'background-color: #ddb3d7;',
            hexa: '#ddb3d7',
          };
        default:
          return null;
      }
    },
    expired() {
      return this.data.daysCount < 0 || this.data.daysCount > 20;
    },
    isAssistantServiceType() {
      return this.serviceType === ServiceTypes.Assistant;
    },
    canApproveServiceAssistant() {
      const isNew = this.data.serviceStatusId === ServiceStatuses.New;

      return (
        this.isAssistantServiceType &&
        isNew &&
        this.userHasPermissions('rempro.admin.action.approveservice')
      );
    },
    canApproveService() {
      const hasPermissions = this.userHasPermissions(
        'menu.rempro.view.approveservice'
      );
      const isNotApproved =
        this.data.serviceStatusId !== ServiceStatuses.Approved &&
        this.data.serviceStatusId !== ServiceStatuses.Objected &&
        this.data.serviceStatusId !== ServiceStatuses.Cancelled;

      if (this.data.isReconsideration) {
        return (
          !this.isAssistantServiceType &&
          hasPermissions &&
          isNotApproved &&
          this.userHasPermissions('rempro.providers.requestreconsideration')
        );
      } else {
        return hasPermissions && isNotApproved && !this.isAssistantServiceType;
      }
    },
    canCancelService() {
      const hasPermissions = this.userHasPermissions(
        'menu.rempro.view.cancelservice'
      );
      const isNotCancelled =
        this.data.serviceStatusId !== ServiceStatuses.Cancelled &&
        this.data.serviceStatusId !== ServiceStatuses.New;

      if (this.data.isReconsideration) {
        return (
          hasPermissions &&
          isNotCancelled &&
          this.userHasPermissions('rempro.providers.requestreconsideration')
        );
      } else {
        return hasPermissions && isNotCancelled;
      }
    },
    canObjectService() {
      const hasPermissions = this.userHasPermissions(
        'menu.rempro.view.objectservice'
      );
      const isNotObjected =
        this.data.serviceStatusId !== ServiceStatuses.Objected &&
        this.data.serviceStatusId !== ServiceStatuses.Approved &&
        this.data.serviceStatusId !== ServiceStatuses.Cancelled;

      if (this.data.isReconsideration) {
        return (
          hasPermissions &&
          isNotObjected &&
          this.userHasPermissions('rempro.providers.requestreconsideration')
        );
      } else {
        return hasPermissions && isNotObjected;
      }
    },
    canReturnStatus() {
      return this.isReturnedToSAEEByHiringLack(this.data);
    },
    canReturnToPreviousStatus() {
      const hasPermissions = this.userHasPermissions(
        'menu.rempro.view.returntopreviousstatus'
      );

      const Statuses = [
        ServiceStatuses.Approved,
        ServiceStatuses.Objected,
        ServiceStatuses.Cancelled,
      ];

      const canDoIt = Statuses.includes(this.data.serviceStatusId);

      if (this.data.isReconsideration) {
        return (
          hasPermissions &&
          canDoIt &&
          !this.data.isStudentEgressed &&
          this.userHasPermissions('rempro.providers.requestreconsideration')
        );
      } else {
        return hasPermissions && canDoIt && !this.data.isStudentEgressed;
      }
    },
    canReturnToNewStatus() {
      const hasPermissions = this.userHasPermissions(
        'menu.rempro.view.returntonewstatus'
      );

      const Statuses = [ServiceStatuses.Cancelled];

      const canDoIt = Statuses.includes(this.data.serviceStatusId);

      if (this.data.isReconsideration) {
        return (
          hasPermissions &&
          canDoIt &&
          this.userHasPermissions('rempro.providers.requestreconsideration')
        );
      } else {
        return hasPermissions && canDoIt;
      }
    },
    canViewDetermination() {
      return !!this.data.isDeterminated;
    },
    determinationPanelActive() {
      return this.showDeterminationPanel && this.canViewDetermination;
    },
    canDownloadLetter() {
      return (
        this.data.serviceStatusId === ServiceStatuses.Approved ||
        this.data.serviceStatusId === ServiceStatuses.Objected
      );
    },
    canGetAppointment() {
      return (
        this.serviceType === ServiceTypes.Evaluation ||
        this.serviceType === ServiceTypes.Therapy
      );
    },
    requestHistoryViewDisplay() {
      return requestHistoryViewDisplay;
    },
    serviceType() {
      return this.data.serviceTypeId;
    },
    serviceContractHistory() {
      return this.data.serviceContractHistory?.data || [];
    },
    requestDocument() {
      return this.data.requestDocument || [];
    },
    showServiceContractPagination() {
      return (
        this.serviceContractPagination.count > 0 &&
        this.serviceContractPagination.pageCount > 1
      );
    },
    serviceContractPagination() {
      let pagination = this.data.serviceContractHistory;
      delete pagination.data;
      pagination = {
        ...pagination,
        pageSize: DEFAULT_HISTORY_CONTRACT_PAGE_SIZE,
      };
      return pagination;
    },
    showPrintApproval() {
      return (
        [ServiceStatuses.Approved, ServiceStatuses.Objected].includes(
          this.data?.serviceStatusId
        ) &&
        this.userHasPermissions('rempro.providers.request.canmarkprinted') &&
        (this.data.hasValidApplicantEmail == null ||
          this.data.hasValidApplicantEmail === true)
      );
    },
    canRequestReconsideration() {
      return (
        this.data.serviceStatusId == ServiceStatuses.Cancelled &&
        this.isReturnedToSAEEByHiringLack(this.data) &&
        (this.data.compensatoryMaxVisitCount == null ||
          this.data.compensatoryMaxVisitCount == 0)
      );
    },
    canSeeBinnacle() {
      return this.userHasPermissions('rempro.bitacora.tabs');
    },
  },
  methods: {
    async fetchBitacoraHistory(requestId) {
      const { data } = await getBitacoraItems(requestId);
      this.blogItems = data;
    },
    async onServiceSaved(saved, row) {
      if (saved) {
        const { data } = await getRequestServicesById(row.requestId);
        this.showCustomTab = true;
        this.$emit('on-save-determination', data);
      }
    },
    onMarkAsPrintedHandler({ approvedForPrinting, requestServiceId }) {
      if (!approvedForPrinting) {
        this.$emit('on-approve-for-printing', [requestServiceId]);
      }
    },
    onCloseHandler() {
      this.showCustomTab = true;
      this.$emit('on-open-close');
    },
    onOpenStatusHistory() {
      this.showDeterminationPanel = false;
      this.$emit('on-open-status-history');
    },
    onOpenContractHistory(pageIndex = 1) {
      this.showDeterminationPanel = false;
      this.$emit('on-open-contract-history', {
        pageIndex: pageIndex,
        pageSize: DEFAULT_HISTORY_CONTRACT_PAGE_SIZE,
      });
    },
    onOpenRequestDocument() {
      this.showDeterminationPanel = false;
      this.$emit('on-open-request-document');
    },
    onChangePageHandler(page) {
      this.onOpenContractHistory(page);
    },
    onOpenViewDeterminationHandler() {
      this.showCustomTab = true;
      this.showDeterminationPanel = true;
      this.$emit(
        'on-open-view-determination',
        this.data.serviceStatusId,
        this.serviceType
      );
    },
    onDownloadDocumentHandler({ documentId }) {
      if (documentId) {
        _downloadDocumentByAttachmentId(documentId).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });
      }
    },
    isReturnedToSAEEByHiringLack(data) {
      return (
        data.determinationReasonId ===
        determinationReasons.ReturnedToSAEEByHiringLack
      );
    },
    onDeterminationSave() {
      this.$emit('on-refresh');
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';
.edit-Row {
  padding-top: 10px !important;
}

.border-radius--light {
  border-radius: 0px !important;
}

.badge-green-light {
  background-color: map-get($colors, 'green-light');
}
.badge-purple {
  background-color: map-get($colors, 'violet');
}
.badge-yellow {
  background-color: map-get($colors, 'yellow');
}
.badge-brown {
  background-color: map-get($colors, 'brown');
}
.border-solid-1 {
  border: 1px solid;
}
.badge-success {
  background-color: map-get($colors, 'secondary-1');
}
.badge-error {
  background-color: map-get($colors, 'red-light');
}

.circle-dot {
  width: 13px;
  height: 13px;
  background-color: map-get($colors, 'white');
  border-radius: 50%;
  display: inline-block;
}

@media screen and (max-width: 768px) {
  #statusBadge {
    display: none;
  }
  #statusCircleBadge {
    display: block !important;
  }
  #printApproval {
    display: none !important;
  }

  #buttonsContainer {
    display: block !important;
  }

  hr {
    display: block !important;
  }

  ::v-deep #dropdownMenuButton {
    width: 97% !important;
    display: block !important;
  }

  ::v-deep div.dropdown-menu {
    width: 80% !important;
    top: 0 !important;
  }
}
</style>
